import { type EventDto, RECLASSIFIABLE_SEVERITIES, type ReclassifiableSeverity } from '@fos/shared/contracts/inbox';
import {
  MAIL_RECLASSIFY_LABELS,
  RECLASSIFY_MAIL_HINTS,
  RECLASSIFY_SEVERITY_HINTS,
  SEVERITY_LABELS,
} from '@fos/shared/labels';
import { useEffect, useState } from 'react';
import { errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Textarea } from '../../../ui/Input';
import { Segmented } from '../../../ui/Segmented';
import { toast } from '../../../ui/Toast';
import { useInboxRefresh } from '../hooks';

function initialSeverity(event: Pick<EventDto, 'severity'>): ReclassifiableSeverity {
  return (RECLASSIFIABLE_SEVERITIES as readonly string[]).includes(event.severity)
    ? (event.severity as ReclassifiableSeverity)
    : 'normal';
}

/**
 * „Zmień ważność” (MVP §9: właściciel poprawia klasyfikację; owner/admin/manager): Pilne / Zwykłe / Do wiadomości
 * z opisem skutku i powodem. Gdy ważność jest już poprawiona — „Przywróć klasyfikację źródła”. Odebrany mail
 * (17.10.2026): te same wartości jako Pilne / Istotne / Mniej ważne (mail „Mniej ważne” zostaje w Inboxie).
 */
export function ReclassifyDialog({
  open,
  onOpenChange,
  event,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: Pick<EventDto, 'id' | 'severity' | 'originalSeverity' | 'override'> & Partial<Pick<EventDto, 'type'>>;
}) {
  const mail = event.type === 'message.received';
  const labels: Record<string, string> = mail ? { ...SEVERITY_LABELS, ...MAIL_RECLASSIFY_LABELS } : SEVERITY_LABELS;
  const hints = mail ? RECLASSIFY_MAIL_HINTS : RECLASSIFY_SEVERITY_HINTS;
  const refresh = useInboxRefresh();
  const [severity, setSeverity] = useState<ReclassifiableSeverity>(() => initialSeverity(event));
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const mutation = trpc.inbox.reclassify.useMutation();

  // reset tylko przy otwarciu (odświeżenie danych w tle nie kasuje wpisanego powodu)
  // biome-ignore lint/correctness/useExhaustiveDependencies: celowo tylko `open`
  useEffect(() => {
    if (!open) return;
    setSeverity(initialSeverity(event));
    setReason('');
    setReasonError(null);
    setFormError(null);
  }, [open]);

  const unchanged = severity === event.severity;

  const submit = async (next: ReclassifiableSeverity | null) => {
    setFormError(null);
    if (next !== null && !reason.trim()) {
      setReasonError('Napisz krótko, dlaczego zmieniasz');
      return;
    }
    try {
      const dto = await mutation.mutateAsync({
        eventId: event.id,
        severity: next,
        ...(next !== null ? { reason: reason.trim() } : {}),
      });
      await refresh();
      onOpenChange(false);
      toast.success(
        next === null
          ? `Przywrócono ważność „${labels[dto.severity]}”`
          : dto.severity === 'fyi' && !mail
            ? 'Przeniesiono do historii klienta'
            : `Ważność: ${labels[dto.severity]}`,
      );
    } catch (err) {
      const issue = validationIssues(err).find((i) => i.path === 'reason');
      if (issue) setReasonError(issue.message);
      else setFormError(errorMessage(err, 'Nie udało się zmienić ważności.'));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Zmień ważność"
      description={
        mail
          ? 'Istotność decyduje, czy mail liczy się do plakietki i czy wysyła powiadomienie na telefon.'
          : 'Ważność decyduje, czy wiadomość jest w Inboxie i czy wysyła powiadomienie na telefon.'
      }
      footer={
        <>
          {event.override && (
            <Button variant="ghost" className="mr-auto" disabled={mutation.isPending} onClick={() => void submit(null)}>
              Przywróć: {labels[event.originalSeverity]}
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={unchanged}
            onClick={() => void submit(severity)}
          >
            Zapisz
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {formError && <FormMessage>{formError}</FormMessage>}
        <Field label="Ważność" hint={hints[severity]}>
          <Segmented<ReclassifiableSeverity>
            aria-label="Ważność"
            fullWidth
            value={severity}
            onValueChange={setSeverity}
            options={RECLASSIFIABLE_SEVERITIES.map((s) => ({ value: s, label: labels[s] }))}
          />
        </Field>
        <Field label="Powód" error={reasonError}>
          <Textarea
            rows={3}
            value={reason}
            maxLength={500}
            placeholder="Np. klient zgłasza awarię produkcji"
            onChange={(e) => {
              setReason(e.target.value);
              if (reasonError) setReasonError(null);
            }}
          />
        </Field>
      </div>
    </Dialog>
  );
}
